import React, { useState, useMemo } from 'react';
import MultiSelectDropdown from '../MultiSelectDropdown';

// --- ICONOS SVG ---
const IconChevronDown = ({ className = "w-3.5 h-3.5 text-slate-400" }) => (
    <svg className={`${className} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const IconHourglass = () => (
    <svg className="w-4 h-4 text-fuchsia-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconShield = () => (
    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const IconUsers = () => (
    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);

const IconReceipt = () => (
    <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
);

/**
 * FiltrosRenovaciones - Submódulo modular de filtros para el módulo de Renovaciones.
 */
const FiltrosRenovaciones = ({
    opcionesFiltrosUnicas = {},
    multiFiltros = {},
    setMultiFiltros,
    activeDropdown,
    setActiveDropdown
}) => {
    const [seccionesAbiertas, setSeccionesAbiertas] = useState({
        renovaciones: true,
        riesgo: true,
        comercial: false,
        gestion: false
    });

    const toggleSeccion = (seccion) => {
        setSeccionesAbiertas(prev => ({ ...prev, [seccion]: !prev[seccion] }));
    };

    const {
        estadoGestionRenovacion = [],
        quienRenovo = [],
        tipoRenovacion = [],
        formaRenovacion = [],
        estadoEmision = [],
        motivo = [],
        subOperacion = [],
        compania = [],
        riesgo = [],
        subriesgo = [],
        vendedor = [],
        suscriptor = [],
        canal = [],
        canalNombre = [],
        subcanalNombre = [],
        tipoFacturacion = [],
        formaPago = [],
        moneda = []
    } = opcionesFiltrosUnicas;

    const contadorRenovaciones = useMemo(() => {
        return (multiFiltros.estadoGestionRenovacion?.length || 0) +
               (multiFiltros.quienRenovo?.length || 0) +
               (multiFiltros.tipoRenovacion?.length || 0) +
               (multiFiltros.formaRenovacion?.length || 0);
    }, [multiFiltros]);

    const contadorRiesgo = useMemo(() => {
        return (multiFiltros.estadoEmision?.length || 0) +
               (multiFiltros.motivo?.length || 0) +
               (multiFiltros.subOperacion?.length || 0) +
               (multiFiltros.compania?.length || 0) +
               (multiFiltros.riesgo?.length || 0) +
               (multiFiltros.subriesgo?.length || 0);
    }, [multiFiltros]);

    const contadorComercial = useMemo(() => {
        return (multiFiltros.vendedor?.length || 0) +
               (multiFiltros.suscriptor?.length || 0) +
               (multiFiltros.canal?.length || 0) +
               (multiFiltros.canalNombre?.length || 0) +
               (multiFiltros.subcanalNombre?.length || 0);
    }, [multiFiltros]);

    const contadorGestion = useMemo(() => {
        return (multiFiltros.tipoFacturacion?.length || 0) +
               (multiFiltros.formaPago?.length || 0) +
               (multiFiltros.moneda?.length || 0);
    }, [multiFiltros]);

    return (
        <div className="space-y-4 animate-fade-in">
            {/* 1. SECCIÓN PRINCIPAL: GESTIÓN DE RENOVACIONES */}
            <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                <button
                    type="button"
                    onClick={() => toggleSeccion('renovaciones')}
                    className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                        seccionesAbiertas.renovaciones ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <IconHourglass />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Gestión de Renovaciones</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {contadorRenovaciones > 0 && (
                            <span className="text-[9px] px-2 py-0.5 bg-fuchsia-100 text-fuchsia-800 font-extrabold rounded-full">
                                {contadorRenovaciones}
                            </span>
                        )}
                        <IconChevronDown className={seccionesAbiertas.renovaciones ? 'rotate-180' : ''} />
                    </div>
                </button>
                
                {seccionesAbiertas.renovaciones && (
                    <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                        <MultiSelectDropdown 
                            label="Estado de Renovación" 
                            options={estadoGestionRenovacion} 
                            selectedValues={multiFiltros.estadoGestionRenovacion || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, estadoGestionRenovacion: vals }))} 
                            isOpen={activeDropdown === 'estadoGestionRenovacion'}
                            onToggle={(open) => setActiveDropdown(open ? 'estadoGestionRenovacion' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Quién Renovó" 
                            options={quienRenovo} 
                            selectedValues={multiFiltros.quienRenovo || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, quienRenovo: vals }))} 
                            isOpen={activeDropdown === 'quienRenovo'}
                            onToggle={(open) => setActiveDropdown(open ? 'quienRenovo' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Tipo de Renovación" 
                            options={tipoRenovacion} 
                            selectedValues={multiFiltros.tipoRenovacion || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, tipoRenovacion: vals }))} 
                            isOpen={activeDropdown === 'tipoRenovacion'}
                            onToggle={(open) => setActiveDropdown(open ? 'tipoRenovacion' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Forma de Renovación" 
                            options={formaRenovacion} 
                            selectedValues={multiFiltros.formaRenovacion || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, formaRenovacion: vals }))} 
                            isOpen={activeDropdown === 'formaRenovacion'}
                            onToggle={(open) => setActiveDropdown(open ? 'formaRenovacion' : null)}
                        />
                    </div>
                )}
            </div>

            {/* 2. SECCIÓN: ESTRUCTURA DEL RIESGO */}
            <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                <button
                    type="button"
                    onClick={() => toggleSeccion('riesgo')}
                    className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                        seccionesAbiertas.riesgo ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <IconShield />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Estructura del Riesgo</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {contadorRiesgo > 0 && (
                            <span className="text-[9px] px-2 py-0.5 bg-indigo-100 text-indigo-800 font-extrabold rounded-full">
                                {contadorRiesgo}
                            </span>
                        )}
                        <IconChevronDown className={seccionesAbiertas.riesgo ? 'rotate-180' : ''} />
                    </div>
                </button>
                
                {seccionesAbiertas.riesgo && (
                    <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                        <MultiSelectDropdown 
                            label="Estado de Emisión" 
                            options={estadoEmision} 
                            selectedValues={multiFiltros.estadoEmision || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, estadoEmision: vals }))} 
                            isOpen={activeDropdown === 'estadoEmision'}
                            onToggle={(open) => setActiveDropdown(open ? 'estadoEmision' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Tipo de Operación (Motivo)" 
                            options={motivo} 
                            selectedValues={multiFiltros.motivo || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, motivo: vals }))} 
                            isOpen={activeDropdown === 'motivo'}
                            onToggle={(open) => setActiveDropdown(open ? 'motivo' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Sub Operación" 
                            options={subOperacion} 
                            selectedValues={multiFiltros.subOperacion || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, subOperacion: vals }))} 
                            isOpen={activeDropdown === 'subOperacion'}
                            onToggle={(open) => setActiveDropdown(open ? 'subOperacion' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Compañía Aseguradora" 
                            options={compania} 
                            selectedValues={multiFiltros.compania || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, compania: vals }))} 
                            isOpen={activeDropdown === 'compania'}
                            onToggle={(open) => setActiveDropdown(open ? 'compania' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Ramo Principal" 
                            options={riesgo} 
                            selectedValues={multiFiltros.riesgo || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, riesgo: vals }))} 
                            isOpen={activeDropdown === 'riesgo'}
                            onToggle={(open) => setActiveDropdown(open ? 'riesgo' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Subriesgo" 
                            options={subriesgo} 
                            selectedValues={multiFiltros.subriesgo || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, subriesgo: vals }))} 
                            isOpen={activeDropdown === 'subriesgo'}
                            onToggle={(open) => setActiveDropdown(open ? 'subriesgo' : null)}
                        />
                    </div>
                )}
            </div>

            {/* 3. SECCIÓN: ESTRUCTURA COMERCIAL */}
            <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                <button
                    type="button"
                    onClick={() => toggleSeccion('comercial')}
                    className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                        seccionesAbiertas.comercial ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <IconUsers />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Estructura Comercial</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {contadorComercial > 0 && (
                            <span className="text-[9px] px-2 py-0.5 bg-emerald-100 text-emerald-800 font-extrabold rounded-full">
                                {contadorComercial}
                            </span>
                        )}
                        <IconChevronDown className={seccionesAbiertas.comercial ? 'rotate-180' : ''} />
                    </div>
                </button>
                
                {seccionesAbiertas.comercial && (
                    <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                        <MultiSelectDropdown 
                            label="Vendedor" 
                            options={vendedor} 
                            selectedValues={multiFiltros.vendedor || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, vendedor: vals }))} 
                            isOpen={activeDropdown === 'vendedor'}
                            onToggle={(open) => setActiveDropdown(open ? 'vendedor' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Suscriptor" 
                            options={suscriptor} 
                            selectedValues={multiFiltros.suscriptor || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, suscriptor: vals }))} 
                            isOpen={activeDropdown === 'suscriptor'}
                            onToggle={(open) => setActiveDropdown(open ? 'suscriptor' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Tipo de Canal" 
                            options={canal} 
                            selectedValues={multiFiltros.canal || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, canal: vals }))} 
                            isOpen={activeDropdown === 'canal'}
                            onToggle={(open) => setActiveDropdown(open ? 'canal' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Nombre del Canal" 
                            options={canalNombre} 
                            selectedValues={multiFiltros.canalNombre || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, canalNombre: vals }))} 
                            isOpen={activeDropdown === 'canalNombre'}
                            onToggle={(open) => setActiveDropdown(open ? 'canalNombre' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Subcanal / Asociado" 
                            options={subcanalNombre} 
                            selectedValues={multiFiltros.subcanalNombre || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, subcanalNombre: vals }))} 
                            isOpen={activeDropdown === 'subcanalNombre'}
                            onToggle={(open) => setActiveDropdown(open ? 'subcanalNombre' : null)}
                        />
                    </div>
                )}
            </div>

            {/* 4. SECCIÓN: GESTIÓN Y FACTURACIÓN */}
            <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                <button
                    type="button"
                    onClick={() => toggleSeccion('gestion')}
                    className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                        seccionesAbiertas.gestion ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <IconReceipt />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Gestión y Facturación</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {contadorGestion > 0 && (
                            <span className="text-[9px] px-2 py-0.5 bg-amber-100 text-amber-800 font-extrabold rounded-full">
                                {contadorGestion}
                            </span>
                        )}
                        <IconChevronDown className={seccionesAbiertas.gestion ? 'rotate-180' : ''} />
                    </div>
                </button>
                
                {seccionesAbiertas.gestion && (
                    <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                        <MultiSelectDropdown 
                            label="Tipo de Facturación" 
                            options={tipoFacturacion} 
                            selectedValues={multiFiltros.tipoFacturacion || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, tipoFacturacion: vals }))} 
                            isOpen={activeDropdown === 'tipoFacturacion'}
                            onToggle={(open) => setActiveDropdown(open ? 'tipoFacturacion' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Forma de Pago" 
                            options={formaPago} 
                            selectedValues={multiFiltros.formaPago || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, formaPago: vals }))} 
                            isOpen={activeDropdown === 'formaPago'}
                            onToggle={(open) => setActiveDropdown(open ? 'formaPago' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Moneda" 
                            options={moneda} 
                            selectedValues={multiFiltros.moneda || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, moneda: vals }))} 
                            isOpen={activeDropdown === 'moneda'}
                            onToggle={(open) => setActiveDropdown(open ? 'moneda' : null)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FiltrosRenovaciones;